import React, { FC, useRef, useState, useContext } from "react";
import styled from "styled-components";
import SelectInput from "../fields/SelectInput";
import WithLabel from "../fields/WithLabel";
import Icon from "../reviews/Icon";
import { GRAY } from "../styles/constants";
import TextInput from "../fields/TextInput";
import TextareaInput from "../fields/TextareaInput";

import { Mutation } from "react-apollo";
import gql from "graphql-tag";

import { Formik, Form as FormikForm } from "formik";
import { string, object } from "yup";
import Button from "../common/Button";
import Policy from "../common/Policy";

import { createReview } from "../../__generated__/createReview";
import getUndef from "../../lib/get-undef";
import useReviewsShops from "../hooks/use-reviews-shops";

const POST_REVIEW = gql`
  mutation createReview(
    $fullName: String!
    $feedback: String
    $type: ReviewTypes!
    $phone: String
    $shop: ID!
  ) {
    createReview(
      data: {
        fullName: $fullName
        feedback: $feedback
        type: $type
        phone: $phone
        shop: { connect: { id: $shop } }
      }
    ) {
      feedback
    }
  }
`;

const options = [
  {
    value: "SUPER",
    name: "Все супер",
  },
  {
    value: "POOR",
    name: "У меня есть предложение",
  },
  {
    value: "BAD",
    name: "Все плохо",
  },
];
const optionsWithLabels = options.map(({ value, name }) => ({
  value,
  label: () => (
    <OptionRow>
      <OptionIcon>
        <Icon code={value} size={22} />
      </OptionIcon>
      <OptionLabel>{name}</OptionLabel>
    </OptionRow>
  ),
}));

const ReviewForm: FC<{}> = () => {
  const { data } = useReviewsShops();

  const placeOptionsWithLabels = getUndef(() => {
    const places = data.reviewsShops.reduce((prev, shop) => {
      const item = {
        value: shop.id,
        label: () => (
          <PlaceLabel>
            <p className="name">{shop.name}</p>
          </PlaceLabel>
        ),
      };

      switch (shop.name) {
        case "Тест":
          break;
        default:
          prev.push(item);
          break;
      }

      return prev;
    }, []);

    return places;
  });

  const resetFormRef = useRef(null);
  const [send, setSend] = useState(false);
  return (
    <Mutation<createReview>
      mutation={POST_REVIEW}
      refetchQueries={["gerReviews"]}
      onCompleted={() => {
        resetFormRef.current();
        setSend(true);
      }}
    >
      {(createReview, { data, error, loading }) => {
        return (
          <>
            {!send ? (
              <Formik
                enableReinitialize
                initialValues={{
                  rating: optionsWithLabels[0].value,
                  name: "",
                  restaurant: getUndef(
                    () => placeOptionsWithLabels[0].value || ""
                  ),
                  text: "",
                  phone: "",
                }}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false);
                  const { name, text, restaurant, rating, phone } = values;
                  createReview({
                    variables: {
                      fullName: name,
                      feedback: text,
                      shop: restaurant,
                      type: rating,
                      phone,
                    },
                  });
                }}
                validationSchema={object().shape({
                  rating: string().required("Required"),
                  name: string().required("Required"),
                  restaurant: string().required("Required"),
                  phone: string().required("Required"),
                  text: string(),
                })}
              >
                {(props) => {
                  const {
                    values,
                    touched,
                    errors,
                    dirty,
                    isSubmitting,
                    handleChange,
                    setFieldValue,
                    setFieldTouched,
                    handleBlur,
                    handleReset,
                  } = props;

                  if (!resetFormRef.current) {
                    resetFormRef.current = handleReset;
                  }

                  return (
                    <FormikForm translate="no">
                      <Form>
                        <h4>Оставить отзыв</h4>
                        <>
                          <WithLabel label="Ваша оценка">
                            <SelectInput
                              value={values.rating}
                              options={optionsWithLabels}
                              onChange={(value) => {
                                setFieldValue("rating", value);
                              }}
                              onBlur={() => {
                                setFieldTouched("rating");
                              }}
                              error={errors.rating && touched.rating}
                            />
                          </WithLabel>
                          <WithLabel label="Имя *" htmlFor="review-name">
                            <TextInput
                              id="review-name"
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.name && touched.name}
                            />
                          </WithLabel>
                          <WithLabel label="Телефон *" htmlFor="review-phone">
                            <TextInput
                              id="review-phone"
                              name="phone"
                              mask="+7 999-999-99-99"
                              value={values.phone}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.phone && touched.phone}
                            />
                          </WithLabel>
                          <WithLabel label="Отзыв о ресторане">
                            <SelectInput
                              value={values.restaurant}
                              options={placeOptionsWithLabels}
                              onChange={(value) => {
                                setFieldValue("restaurant", value);
                              }}
                              onBlur={() => {
                                setFieldTouched("restaurant");
                              }}
                              error={Boolean(
                                errors.restaurant && touched.restaurant
                              )}
                            />
                          </WithLabel>
                          <WithLabel label="Текст отзыва" htmlFor="review-text">
                            <TextareaInput
                              id="review-text"
                              name="text"
                              value={values.text}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.text && touched.text}
                            />
                          </WithLabel>
                          <SubmitButton
                            type="submit"
                            disabled={
                              !values.name ||
                              !values.text ||
                              values.phone.length < 16
                            }
                            bold
                            primary
                            full
                            big
                            loading={loading}
                          >
                            ОСТАВИТЬ ОТЗЫВ
                          </SubmitButton>
                        </>
                        <Policy center name="Оставить отзыв" />
                      </Form>
                    </FormikForm>
                  );
                }}
              </Formik>
            ) : (
              <MessageSend>
                <ReturnIcon
                  src="./static/icons/return.png"
                  alt="назад"
                  onClick={() => setSend(false)}
                />
                <Icon code="SUPER" size={38} />
                <MainTextBlock className="kabel">
                  <span>Спасибо,</span> <br />
                  что помогаете нам стать лучше!
                </MainTextBlock>
                <TextBlock>
                  Ваш отзыв будет опубликован <br /> в скором времени!{" "}
                </TextBlock>
              </MessageSend>
            )}
          </>
        );
      }}
    </Mutation>
  );
};

const MainTextBlock = styled.p`
  font-size: 26px;
  color: #fff;
  line-height: 35px;
  span {
    font-size: 36px;
  }
`;

const TextBlock = styled.p`
  color: #fff;
  font-size: 13px;
`;

const MessageSend = styled.div`
  position: relative;
  background: #78b833;
  border-radius: 15px;
  text-align: center;
  position: relative;
  background: #78b833;
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  padding: 80px 60px;
`;

const ReturnIcon = styled.img`
  position: absolute;
  right: 20px;
  top: 20px;
  cursor: pointer;
`;

const Form = styled.div`
  background: #fff;
  border-radius: 15px;
  padding: 26px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
`;

const OptionIcon = styled.span`
  display: inline-block;
  margin-right: 8px;
`;

const OptionLabel = styled.span`
  display: inline-block;
`;

const PlaceLabel = styled.div`
  .name {
    font-size: 14px;
    margin: 0;
  }

  .address {
    font-size: 11px;
    margin: 0;
    color: ${GRAY};
  }
`;

const SubmitButton = styled(Button)`
  margin: 26px 0 20px;
  padding: 0;
`;

export default ReviewForm;
